import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useEffect, useId, useRef, useState } from 'react';
import { getErrorMessage, normalizeError } from '@/core/error-handling/ApiErrorHandler';
import { useImportProgress } from './ImportProgressContext';
import { MasterHintTooltip, useMasterHint } from './MasterHintTooltip';
import { isImportJob, isImportJobFinished } from '../../models/masterImport.types';
import type {
  MasterImportJob,
  MasterImportOutcome,
  MasterImportResult,
} from '../../models/masterImport.types';

interface MasterImportDialogProps {
  visible: boolean;
  /** What the dialog is importing, for its title: "Organization", "Entity". */
  title: string;
  /**
   * The template's column headings, already joined for reading: "Code · Name · Status".
   *
   * Drawn under the template card so the file's shape is known before it is downloaded. Omitted where the
   * caller has no column list to offer, which leaves the line out rather than printing an empty "Columns:".
   *
   * The two-step card (`templateColumns`) does NOT draw it: a Purchase Request template's file runs to twenty
   * columns, which reads as a paragraph rather than a list. Only the masters, whose files are three or four
   * columns wide, print it.
   */
  columns?: string;
  /**
   * Sends the chosen file, bytes and all. Absent on a master with no import
   * endpoint, which leaves the dialog explaining itself rather than offering a
   * dead control.
   */
  onImport?: (file: File) => Promise<MasterImportOutcome>;
  /**
   * The file layout, where it differs from the export's columns -- Purchase Requests: one row per line item. Drawn
   * under the subtitle.
   */
  hint?: string;
  /**
   * Called after an import that created at least one record, so the list refreshes.
   *
   * Carries the outcome so the screen can confirm it in its own notification slot, the way every other
   * write does. The dialog keeps its detailed report — which rows were rejected and why — because that
   * is something to read and act on, not a transient confirmation.
   *
   * For a queued import this fires when the job finishes, not when the upload is accepted: nothing has
   * been created at the moment the server says "accepted", so refreshing the table then shows the same
   * rows back.
   */
  onImported?: (result: MasterImportOutcome) => void;
  /**
   * Reads a queued import's progress, for a master whose import runs in the background.
   *
   * Absent on the masters that still import synchronously, which is what keeps this one dialog serving
   * both: no poller means the upload's answer was the whole answer.
   */
  onPollJob?: (jobId: number) => Promise<MasterImportJob>;
  /**
   * Downloads the rows a queued import rejected, and hands the file to the browser.
   *
   * The screen does the saving, as it does for the template, because it already names and saves the
   * export's download and one of those is enough.
   */
  onDownloadErrors?: (jobId: number, format: 'xlsx' | 'csv') => Promise<void>;
  /**
   * Downloads the blank template for a format, and hands it to the browser.
   *
   * The screen does the saving rather than the dialog, because it already names and saves the export's
   * download and one of those is enough. Absent on a master with no import endpoint, which leaves the
   * action out rather than offering a download that answers 404.
   */
  onDownloadTemplate?: (format: 'xlsx' | 'csv') => Promise<void>;
  /**
   * The templates this master's file can be filed under, where it has them (Purchase Requests, NB-551).
   *
   * Present, the popup opens on the picker alone: which template the file belongs to decides its columns and
   * the rules its rows are read against, so there is nothing to download or upload until one is chosen.
   * Absent -- every other master -- the popup is exactly as it was, with no picker and nothing gated.
   */
  templateOptions?: readonly { label: string; value: string }[];
  /** The chosen template, '' for none. Controlled by the screen, which also reads its columns. */
  templateValue?: string;
  onTemplateChange?: (value: string) => void;
  /** While the templates are still being read, so the picker says so rather than looking empty. */
  templatesLoading?: boolean;
  /**
   * The footer's Close button. On by default; off, the dialog closes with its header's ×
   * alone (Purchase Requests, 2026-10-09).
   */
  showFooter?: boolean;
  /**
   * design16's two-step layout (Supplier, Purchase Request): "1 Download template" -- a card naming the template's
   * columns with its Excel and CSV downloads -- then "2 Upload filled file". The column names are the card's
   * "Columns:" line. Omitted, the one-step layout every other master draws.
   */
  templateColumns?: readonly string[];
  onHide: () => void;
}

/**
 * The refused rows, saved as the workbook the server already built.
 *
 * Nothing is fetched. The screen does the saving for the template and the export because it is the screen
 * that names those files; this one arrived named, with the import's own answer, so there is nothing left
 * for the screen to decide and no round trip to make.
 */
function saveErrorReport(report: string, name: string): void {
  const binary = window.atob(report);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([bytes], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
}

interface Format {
  icon: string;
  label: string;
  /** The template button's own word, which has only its button to fill: "Excel", "CSV". */
  short: string;
  sub: string;
  accept: string;
  /** What the template endpoint is asked for; the same word the export's format card sends. */
  template: 'xlsx' | 'csv';
}

/** The two the reference offers, with the sentence it puts under each. */
const FORMATS: Format[] = [
  {
    icon: 'pi-table',
    label: 'Excel (.xlsx)',
    short: 'Excel',
    sub: 'Workbook, first sheet is read',
    accept: '.xlsx',
    template: 'xlsx',
  },
  {
    icon: 'pi-file',
    label: 'CSV (.csv)',
    short: 'CSV',
    sub: 'Comma separated, UTF-8',
    accept: '.csv',
    template: 'csv',
  },
];

/** One message for "there is nothing in this file", whichever way the emptiness shows up. */
const EMPTY_FILE_MESSAGE = 'The selected file is empty. Please select a file containing valid data.';

/**
 * Whether anything follows the header row.
 *
 * Blank lines do not count -- a file ending in a newline, which most editors write, is not a file with a
 * record in it. This is deliberately not a CSV parse: it answers one question, and the server does the real
 * parsing and reports per-row rejections from it.
 */
function hasDataRows(csv: string): boolean {
  const lines = csv.split(/\r?\n/).filter((line) => line.trim() !== '');
  return lines.length > 1;
}

/**
 * Whether the chosen file is a workbook, from its first two bytes.
 *
 * Every `.xlsx` is a zip and every zip begins `PK`. The same test the server makes, and made from the bytes
 * rather than the extension or the media type for the same reason: a file renamed `.csv` is still a zip, and
 * browsers label the same upload four different ways.
 */
/** The file's extension, lower-cased, for the tray's "Product · .xlsx" line. */
function extensionOf(file: File): string {
  const dot = file.name.lastIndexOf('.');
  return dot < 0 ? 'csv' : file.name.slice(dot + 1).toLowerCase();
}

/**
 * Whether an import finished with nothing left for the uploader to read.
 *
 * Rows landed, none was refused, and none needed adjusting on the way in. The server puts every row it read
 * into exactly one of `imported` and `rejected`, so an import with no rejections imported the lot.
 *
 * An import that created nothing is not clean: "0 of 0 rows imported" is an outcome worth seeing, not a
 * success to close on.
 */
function isCleanImport(result: MasterImportResult): boolean {
  return result.imported > 0 && result.rejected.length === 0 && (result.warnings ?? []).length === 0;
}

async function isWorkbook(file: File): Promise<boolean> {
  const head = new Uint8Array(await file.slice(0, 2).arrayBuffer());
  return head[0] === 0x50 && head[1] === 0x4b;
}

/**
 * Import: pick a file, upload it, see what landed.
 *
 * The file's columns are the ones that master's **export** writes, so a file
 * exported from this screen round-trips. Every row is attempted; a row whose
 * code already exists is reported rather than overwritten, because a re-imported
 * stale file would otherwise quietly undo whatever had changed since.
 *
 * **Both cards work.** A workbook used to be refused with a message saying to
 * export as CSV instead -- on the grounds that reading `.xlsx` meant a
 * spreadsheet library on both sides. It never did: the server reads one with
 * nothing but the JDK, and had been doing so for the Template screen all along.
 * What actually broke the upload was this dialog reading the file as text before
 * sending it, which turns a zip into replacement characters.
 */
/** How often a queued import is asked how it is getting on. */
const POLL_INTERVAL_MS = 1500;

export function MasterImportDialog({
  visible,
  title,
  columns,
  onImport,
  onImported,
  onPollJob,
  onDownloadErrors,
  onHide,
  hint,
  onDownloadTemplate,
  templateOptions,
  templateValue = '',
  onTemplateChange,
  templatesLoading = false,
  showFooter = true,
  templateColumns,
}: MasterImportDialogProps) {
  /* design16's two-step layout, where the screen asked for it. */
  const steps = templateColumns !== undefined;
  const input = useRef<HTMLInputElement>(null);
  const [accept, setAccept] = useState('');
  const [fileName, setFileName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<MasterImportResult | null>(null);
  /* The queued half: set when the upload answers a job, then replaced by each poll. */
  const [job, setJob] = useState<MasterImportJob | null>(null);
  /* Which template is in flight, so only that button shows a spinner. */
  const [downloading, setDownloading] = useState<string | null>(null);
  /* Which template has come down, for the card's "…template downloaded" line. */
  const [downloaded, setDownloaded] = useState<Format | null>(null);
  const [downloadingErrors, setDownloadingErrors] = useState(false);
  /* Where a queued import goes once this dialog closes. Null outside the app layout. */
  const progress = useImportProgress();
  /*
    The template button's tooltip; see MasterHintTooltip for why not a native `title`.

    Named apart from the `hint` prop above, which is the caller's own note under the drop zone. Both were
    called `hint`, which is a duplicate declaration in one scope: the file did not compile at all.
  */
  const hintTip = useMasterHint();
  const hintId = useId();
  const templateId = useId();

  /*
    The picker is unanswered. Only true where there is a picker at all, so every other master's popup is
    ungated and unchanged.
  */
  const needsTemplate = templateOptions !== undefined && templateValue === '';
  /* The card names the template the file is for, where one was chosen, rather than the screen. */
  const chosenTemplate = templateOptions?.find((option) => option.value === templateValue)?.label;
  /*
    `inert` takes the locked steps out of the tab order and out of the accessibility tree, which the dimming
    and `pointer-events: none` do not: without it a keyboard could still reach a button nobody can see working.
    Every control inside is disabled as well, so the lock does not depend on one mechanism.
  */
  const locked = needsTemplate ? { inert: true } : {};

  /* A reopened dialog starts over rather than showing the last file's outcome. */
  useEffect(() => {
    if (!visible) {
      setAccept('');
      setFileName('');
      setBusy(false);
      setError(null);
      setResult(null);
      setJob(null);
      setDownloaded(null);
    }
  }, [visible]);

  /**
   * Follows a queued import while the dialog is open.
   *
   * Closing the dialog stops the polling and nothing else: the import is a job on the server and carries
   * on without anybody watching, which is the whole point of queuing it. The uploader is told it finished
   * by notification, not by this.
   *
   * `onImported` fires here rather than at upload, because at upload nothing has been created yet.
   */
  useEffect(() => {
    if (!visible || job === null || onPollJob === undefined || isImportJobFinished(job)) {
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void onPollJob(job.jobId)
        .then((next) => {
          if (cancelled) return;
          setJob(next);
          if (!isImportJobFinished(next)) return;
          if (next.imported > 0) onImported?.(next);
          /*
            The same rule the synchronous path follows: a queued import that finished with nothing to
            report closes the dialog, and one with rejections or a failure keeps it open over its report.
            This is only the no-tray fallback -- where there is a tray, the dialog handed the job over and
            closed at upload.
          */
          if (next.status === 'COMPLETED' && next.imported > 0 && next.rejected === 0) onHide();
        })
        .catch((cause: unknown) => {
          if (cancelled) return;
          /*
            A failed poll is not a failed import. The job is still running on the server; all that is lost
            is this dialog's view of it, so it says so rather than reporting the import as broken.
          */
          setError(getErrorMessage(normalizeError(cause)));
        });
    }, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [visible, job, onPollJob, onImported, onHide]);

  /**
   * Select file → validate file → validate data → upload.
   *
   * <p>The first two steps happen here, before anything is sent. An empty file used to be uploaded like any
   * other and came back as "0 of 0 rows imported", which reads as a successful import of nothing. The server
   * refuses it too -- the endpoint is reachable directly, so a check only the browser performs is not a check
   * -- but catching it here means no round trip and a message that names the actual problem.
   */
  const run = async (file: File) => {
    if (!onImport) return;

    setError(null);
    setResult(null);
    setJob(null);

    /* An empty file has no size, so this costs nothing and does not have to read the file first. */
    if (file.size === 0) {
      setError(EMPTY_FILE_MESSAGE);
      return;
    }

    /*
      The two text checks below are a CSV's. A workbook's bytes are a zip, so reading them as text answers
      nothing about its rows -- and counting "lines" in one would refuse a perfectly good file. The server
      makes both checks again on the rows it actually parses out of the sheet, which is where a workbook's
      emptiness can be seen at all.
    */
    if (!(await isWorkbook(file))) {
      const text = await file.text();
      if (text.trim() === '') {
        setError(EMPTY_FILE_MESSAGE);
        return;
      }

      /*
        A header row and nothing under it. The columns are there, so the file is well formed -- it simply
        carries no records, and importing it would create none.
      */
      if (!hasDataRows(text)) {
        setError('The selected file has no data rows. Please select a file containing valid data.');
        return;
      }
    }

    setBusy(true);
    try {
      /* The file itself: reading it as text first is what used to destroy a workbook. */
      const outcome = await onImport(file);
      if (isImportJob(outcome)) {
        /*
          Queued. Nothing has been created yet, so the table is not refreshed here -- whatever follows the
          job does that once it actually finishes.

          Where there is a tray to follow it in, the dialog hands the job over and closes: the import runs
          in the background, and keeping a modal open over it would contradict the one thing the feature
          promises. Without a tray -- a screen rendered outside the layout -- the dialog keeps the job and
          reports it itself, which is why both paths still exist.
        */
        if (progress !== null && onPollJob !== undefined) {
          progress.track({
            job: outcome,
            format: extensionOf(file),
            poll: onPollJob,
            /* So the tray's "N rejected" line is the download rather than a direction to go and find it. */
            downloadErrors:
              onDownloadErrors === undefined
                ? undefined
                : (jobId) => onDownloadErrors(jobId, 'xlsx'),
            /*
              The table learns about the rows when the job finishes, not when the upload is accepted --
              at which point nothing has been created. Reported through the same `onImported` the
              synchronous path uses, so the screen reloads and confirms it the one way it always has.
            */
            onFinished: (finished) => {
              if (finished.imported > 0) onImported?.(finished);
            },
          });
          onHide();
        } else {
          setJob(outcome);
        }
      } else {
        if (outcome.imported > 0) onImported?.(outcome);
        /*
          A clean import is finished business, so the dialog gets out of the way. The screen confirms it in
          its own notification slot the way every other write is confirmed, and holding the dialog open on
          "3 of 3 rows imported" only asks the uploader to dismiss the same news twice.

          Anything still worth reading keeps the dialog open, because the dialog is the only place it is
          written: a rejected row, a row adjusted on the way in, or a file that created nothing at all.
        */
        if (isCleanImport(outcome)) {
          onHide();
        } else {
          setResult(outcome);
        }
      }
    } catch (err: unknown) {
      setError(getErrorMessage(normalizeError(err)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      /* Scoped backdrop: the mask is portalled to <body>, so no ancestor can reach it. */
      maskClassName="ms-mask"
      visible={visible}
      className="ms-dialog ms-import-dialog"
      modal
      draggable={false}
      resizable={false}
      onHide={onHide}
      header={
        <div className="ms-pop__head">
          <span className="ms-pop__icon" aria-hidden="true">
            <i className="pi pi-upload" />
          </span>
          <span className="ms-pop__title">Import {title}</span>
        </div>
      }
    >
      <p className="ms-pop__sub">
        {!onImport
          ? 'Import is not available for this master yet.'
          : steps || onDownloadTemplate !== undefined
            ? 'Download the template, fill in your data, then upload it for mapping and validation.'
            : 'Pick the file format to continue to upload, mapping and validation.'}
      </p>
      {hint ? <p className="ms-pop__note">{hint}</p> : null}

      {/*
        The template picker, on the masters that file their import under one. Mandatory, and nothing follows it
        until it is answered: the template decides the file's columns and the rules its rows are read against,
        so a download or an upload before it would be of the wrong file.
      */}
      {templateOptions === undefined ? null : (
        <div className={`ms-tplpick${needsTemplate ? ' ms-tplpick--needed' : ''}`}>
          <label className="ms-tplpick__label" htmlFor={templateId}>
            Template<span className="ms-tplpick__req" aria-hidden="true">*</span>
          </label>
          <div className="ms-tplpick__box">
            <select
              id={templateId}
              className="ms-tplpick__select"
              required
              value={templateValue}
              disabled={templatesLoading || templateOptions.length === 0}
              aria-describedby={needsTemplate ? `${templateId}-help` : undefined}
              onChange={(event) => onTemplateChange?.(event.target.value)}
            >
              <option value="">
                {templatesLoading
                  ? 'Loading templates…'
                  : templateOptions.length === 0
                    ? 'No active templates'
                    : 'Select a template'}
              </option>
              {templateOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <i className="pi pi-chevron-down ms-tplpick__chevron" aria-hidden="true" />
          </div>
          {needsTemplate ? (
            <p className="ms-tplpick__help" id={`${templateId}-help`}>
              Select the template to enable download and upload.
            </p>
          ) : null}
        </div>
      )}

      <input
        ref={input}
        type="file"
        className="ms-upload__input"
        accept={accept}
        onChange={(event) => {
          const file = event.target.files?.[0];
          setFileName(file?.name ?? '');
          if (file) void run(file);
          /* Clear the value so choosing the same file twice fires change again. */
          event.target.value = '';
        }}
      />

      {/*
        Step one: the blank template.

        Drawn only where there is one to download. A master with no template endpoint keeps the plain
        upload cards it always had rather than a numbered step that leads nowhere -- and with step one
        gone the numbers go too, since "2" on its own numbers nothing.
      */}
      {/*
        Both steps, dimmed and unusable until the template is answered -- the reference's own treatment. They
        are shown rather than hidden so the popup says what it will ask for, and `inert` takes them out of the
        tab order as well as out of reach of the pointer, which opacity alone would not.
      */}
      <div className={`ms-import__steps${needsTemplate ? ' ms-import__steps--locked' : ''}`} {...locked}>
      {/* design16's two steps (Supplier, Purchase Request -- NB-532); every other master draws the reference's below. */}
      {steps && onImport ? (
        <>
          <p className="ms-import__step">
            <span className="ms-import__step-no">1</span>
            <span>Download template</span>
          </p>
          <div className="ms-import__template">
            <i className="pi pi-file-excel ms-import__template-glyph" aria-hidden="true" />
            <div className="ms-import__template-text">
              <span className="ms-import__template-title">{chosenTemplate ?? title} import template</span>
              {/*
                No column list here (2026-10-28).

                A PR Template's file runs to twenty columns -- the hierarchy, every header field, the line's
                masters, every line field -- and the line wrapped into a paragraph of names that nobody read and
                that repeated itself where a system field is both a header and a line field. The file itself says
                what it holds, in the order it holds it, and the download is one click away. The `columns` prop
                stays for the single-step card below, where the masters' lists are three or four names long.
              */}
            </div>
            {onDownloadTemplate === undefined ? null : (
              <div className="ms-import__template-actions">
                {FORMATS.map((format) => (
                  <button
                    key={format.template}
                    type="button"
                    className="ms-import__template-btn"
                    disabled={needsTemplate || busy || downloading !== null}
                    onClick={() => {
                      setError(null);
                      setDownloading(format.template);
                      void onDownloadTemplate(format.template)
                        .catch((cause: unknown) =>
                          setError(
                            cause instanceof Error ? cause.message : 'The sample template could not be downloaded.',
                          ),
                        )
                        .finally(() => setDownloading(null));
                    }}
                  >
                    <i
                      className={`pi ${downloading === format.template ? 'pi-spin pi-spinner' : 'pi-download'}`}
                      aria-hidden="true"
                    />
                    <span>{format.template === 'xlsx' ? 'Excel' : 'CSV'}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <p className="ms-import__step">
            <span className="ms-import__step-no">2</span>
            <span>Upload filled file</span>
          </p>
        </>
      ) : onDownloadTemplate === undefined ? null : (
        <>
          <div className="ms-step">
            <span className="ms-step__num" aria-hidden="true">
              1
            </span>
            <h3 className="ms-step__label">Download template</h3>
          </div>

          <div className="ms-tpl">
            <i className="pi pi-file-excel ms-tpl__glyph" aria-hidden="true" />
            <div className="ms-tpl__text">
              <span className="ms-tpl__title">{chosenTemplate ?? title} import template</span>
              {columns === undefined || columns === '' ? null : (
                <span className="ms-tpl__cols">Columns: {columns}</span>
              )}
              {downloaded === null ? null : (
                <span className="ms-tpl__done" role="status">
                  <i className="pi pi-check-circle" aria-hidden="true" />
                  {downloaded.short} template downloaded
                </span>
              )}
            </div>

            <div className="ms-tpl__actions">
              {FORMATS.map((format) => (
                <button
                  type="button"
                  key={format.template}
                  className="ms-tpl__btn"
                  disabled={needsTemplate || busy || downloading !== null}
                  /*
                    A tooltip of the application's own rather than a `title`. The native one waits about a
                    second, draws in the operating system's style, and inside a modal is easy to miss --
                    which is what "the tooltip does not show properly" was. Pointer and keyboard both, so
                    it is not a tooltip only a mouse can open.
                  */
                  aria-describedby={hintTip.hint === null ? undefined : hintId}
                  aria-label={`Download ${format.label} sample template`}
                  onMouseEnter={(event) => {
                    hintTip.show(event.currentTarget, `Download ${format.label} sample template`);
                  }}
                  onMouseLeave={hintTip.hide}
                  onFocus={(event) => {
                    hintTip.show(event.currentTarget, `Download ${format.label} sample template`);
                  }}
                  onBlur={hintTip.hide}
                  onClick={() => {
                    setError(null);
                    setDownloading(format.template);
                    void onDownloadTemplate(format.template)
                      .then(() => setDownloaded(format))
                      .catch((cause: unknown) =>
                        setError(
                          cause instanceof Error
                            ? cause.message
                            : 'The sample template could not be downloaded.',
                        ),
                      )
                      .finally(() => setDownloading(null));
                  }}
                >
                  <i
                    className={`pi ${
                      downloading === format.template ? 'pi-spin pi-spinner' : 'pi-download'
                    }`}
                    aria-hidden="true"
                  />
                  {format.short}
                </button>
              ))}
            </div>
          </div>

          <div className="ms-step">
            <span className="ms-step__num" aria-hidden="true">
              2
            </span>
            <h3 className="ms-step__label">Upload filled file</h3>
          </div>
        </>
      )}

      <div className="ms-picks">
        {FORMATS.map((format) => (
          <button
            key={format.label}
            type="button"
            className="ms-pick"
            disabled={needsTemplate || !onImport || busy}
            onClick={() => {
              setAccept(format.accept);
              setFileName('');
              setResult(null);
              setError(null);
              /* Set on the element too: React has not re-rendered `accept` yet. */
              if (input.current) {
                input.current.accept = format.accept;
                input.current.click();
              }
            }}
          >
            <i className={`pi ${format.icon} ms-pick__glyph`} aria-hidden="true" />
            <span className="ms-pick__text">
              <span className="ms-pick__title">{format.label}</span>
              <span className="ms-pick__sub">{format.sub}</span>
            </span>
          </button>
        ))}
      </div>
      </div>

      {/*
        What a queued import means for the person about to start one, before they start it.

        Shown only where the import is actually queued -- `onPollJob` is what says so, and only Product
        passes it. On the seventeen masters that still import synchronously this would be a promise the
        platform does not keep: their upload holds the request open and there is nothing to walk away from.
      */}
      {onImport && onPollJob !== undefined ? (
        <p className="ms-import__hint">
          <i className="pi pi-clock" aria-hidden="true" />
          <span>
            Large files are imported in batches in the background. You can keep working, switch tabs or log
            out. The admin is notified when it&rsquo;s done.
          </span>
        </p>
      ) : null}

      {hintTip.hint === null ? null : <MasterHintTooltip hint={hintTip.hint} id={hintId} />}

      {busy ? (
        <p className="ms-pop__note" role="status">
          <i className="pi pi-spin pi-spinner" aria-hidden="true" /> Importing {fileName}…
        </p>
      ) : null}

      {error === null ? null : (
        <p className="ms-pop__note ms-pop__note--error" role="alert">
          <i className="pi pi-exclamation-circle" aria-hidden="true" /> {error}
        </p>
      )}

      {/*
        A queued import. Counts rather than rows: the rejected rows are a file to download, not a list to
        scroll, and `rejected` here is a number -- which is exactly what used to be handed to `.map`.
      */}
      {job === null ? null : (
        <div className="ms-import__result" role="status">
          <p className="ms-pop__note">
            {isImportJobFinished(job) ? (
              <i
                className={`pi ${job.status === 'FAILED' ? 'pi-times-circle' : 'pi-check-circle'}`}
                aria-hidden="true"
              />
            ) : (
              <i className="pi pi-spin pi-spinner" aria-hidden="true" />
            )}{' '}
            {job.message}
          </p>

          {isImportJobFinished(job) ? null : (
            <p className="ms-pop__note">
              {job.processed} of {job.read} row{job.read === 1 ? '' : 's'} processed. You can close this
              and carry on — the import keeps running, and you will be notified when it finishes.
            </p>
          )}

          {/*
            What was actually wrong, on screen.

            The count alone sent a user to a spreadsheet to read one sentence about one row. The server
            carries the first few rejections for exactly this; past that the count stands and the report
            below is the answer.
          */}
          {(job.rejections ?? []).length === 0 ? null : (
            <ul className="ms-import__rejections">
              {(job.rejections ?? []).map((rejection) => (
                <li key={`j-${String(rejection.line)}-${rejection.code ?? ''}`}>
                  <span className="ms-import__line">Line {rejection.line}</span>
                  {rejection.code === null ? null : (
                    <span className="ms-import__code">{rejection.code}</span>
                  )}
                  <span className="ms-import__reason">{rejection.reason}</span>
                </li>
              ))}
            </ul>
          )}

          {/*
            Offered only where there is something in it. A report of nothing but its header answers a
            question nobody asked.
          */}
          {job.rejected > 0 && onDownloadErrors !== undefined ? (
            <p className="ms-pop__note">
              <Button
                type="button"
                className="ms-btn ms-btn--ghost"
                disabled={downloadingErrors}
                icon={`pi ${downloadingErrors ? 'pi-spin pi-spinner' : 'pi-download'}`}
                label={`Download error report (${String(job.rejected)} row${job.rejected === 1 ? '' : 's'})`}
                onClick={() => {
                  setDownloadingErrors(true);
                  onDownloadErrors(job.jobId, 'xlsx')
                    .catch((cause: unknown) =>
                      setError(
                        cause instanceof Error
                          ? cause.message
                          : 'The error report could not be downloaded.',
                      ),
                    )
                    .finally(() => setDownloadingErrors(false));
                }}
              />
            </p>
          ) : null}
        </div>
      )}

      {result === null ? null : (
        <div className="ms-import__result" role="status">
          <p className="ms-pop__note">
            <i className="pi pi-check-circle" aria-hidden="true" /> {result.imported} of{' '}
            {result.read} row{result.read === 1 ? '' : 's'} imported
            {result.rejected.length > 0 ? `, ${String(result.rejected.length)} rejected` : ''}.
          </p>

          {/*
            Rows that landed, with something adjusted on the way in. Drawn under the count and above the
            rejections: they are not failures, and a reader scanning for what went wrong should reach the
            rejections without these in the way.
          */}
          {(result.warnings ?? []).length === 0 ? null : (
            <ul className="ms-import__warnings">
              {(result.warnings ?? []).map((warning) => (
                <li key={`w-${String(warning.line)}-${warning.code ?? ''}`}>
                  <span className="ms-import__line">Line {warning.line}</span>
                  {warning.code === null ? null : (
                    <span className="ms-import__code">{warning.code}</span>
                  )}
                  <span className="ms-import__reason">{warning.detail}</span>
                </li>
              ))}
            </ul>
          )}

          {result.rejected.length === 0 ? null : (
            <ul className="ms-import__rejections">
              {result.rejected.map((rejection) => (
                <li key={`${String(rejection.line)}-${rejection.code ?? ''}`}>
                  <span className="ms-import__line">Line {rejection.line}</span>
                  {rejection.code === null ? null : (
                    <span className="ms-import__code">{rejection.code}</span>
                  )}
                  <span className="ms-import__reason">{rejection.reason}</span>
                </li>
              ))}
            </ul>
          )}

          {/*
            The same failures as a file to correct and send again, beside the list that explains them.

            The list says what went wrong; the workbook is the uploader's own rows, with an Error
            Description on each one that failed. Reading a reason and then finding that row by eye in a
            two-hundred-row spreadsheet is the part this removes.
          */}
          {result.errorReport ? (
            <p className="ms-pop__note">
              <Button
                type="button"
                className="ms-btn ms-btn--ghost"
                icon="pi pi-download"
                label="Download error report (Excel)"
                onClick={() => {
                  saveErrorReport(
                    result.errorReport ?? '',
                    result.errorReportName ?? 'import-errors.xlsx',
                  );
                }}
              />
            </p>
          ) : null}
        </div>
      )}

      {showFooter ? (
        <div className="ms-pop__footer">
          <Button type="button" className="ms-btn ms-btn--ghost" label="Close" onClick={onHide} />
        </div>
      ) : null}
    </Dialog>
  );
}
